import {
  ProductProvider,
  ProductTitle,
  SelectedVariantPrice,
  useCart,
} from "@shopify/hydrogen";
import type { Product } from "@shopify/hydrogen/dist/esnext/graphql/types/types";
import compact from "lodash/compact";
import { useContext } from "react";

import { useCartUI } from "../../hooks/use-cart-ui";
import { useItemDetails } from "../../hooks/use-item-details";
import type { ItemWithCheckoutProduct } from "../../hooks/use-items-with-checkout";
import { useUTMLink } from "../../hooks/use-utm";
import type {
  CheckoutDisabledConfig,
  CheckoutEnabledConfig,
} from "../../lookbook";
import { SizeContext } from "../../providers/Size";
import type { ProductDataFragment } from "../../shopify-graphql/types";
import { ActionBar } from "../ActionBar";
import { Media } from "../Media";
import { OverlayButton } from "../OverlayButton";
import { ItemContent } from "../item/ItemContent";
import { ItemDetails } from "../item/ItemDetails";
import { OpenAddToBagModalButton } from "../product/OpenAddToBagModalButton";

export interface ItemProps {
  item: ItemWithCheckoutProduct;
  position: number;
  isCurrent: boolean;
  isNext: boolean;
  isPrevious: boolean;
  onPause: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onLongPress?: () => void;
  onLongPressReleased?: () => void;
  checkout: CheckoutEnabledConfig | CheckoutDisabledConfig;
}

export function Item(props: ItemProps) {
  const {
    item,
    position,
    isCurrent,
    isNext,
    isPrevious,
    onPause,
    onNext,
    onLongPress,
    onLongPressReleased,
    onPrevious,
  } = props;

  const { size } = useContext(SizeContext);

  const initialVariant = item.checkoutProduct?.variants.edges[0].node;

  const { checkoutProduct } = item;

  return (
    <section
      data-type="item"
      data-id={item.id}
      data-position={position}
      data-current={isCurrent}
      data-next={isNext}
      data-previous={isPrevious}
      className="lookbook--story--item">
      <Media
        size={size}
        media={item.media}
        visible={isCurrent}
        prefetch={isCurrent || isNext || isPrevious}
        sizes={[
          ["(min-width: 1280px)", "25vw"],
          ["(min-width: 960px)", "33vw"],
          ["(min-width: 768px)", "50vw"],
          ["100vw"],
        ]}
      />
      {onPrevious !== undefined ? (
        <OverlayButton
          position="left"
          onPress={onPrevious}
          onLongPress={onLongPress}
          onLongPressReleased={onLongPressReleased}
        />
      ) : null}
      {onNext !== undefined ? (
        <OverlayButton
          position="right"
          onPress={onNext}
          onLongPress={onLongPress}
          onLongPressReleased={onLongPressReleased}
        />
      ) : null}
      <div className="lookbook--story--item--content">
        {checkoutProduct ? (
          <ProductProvider
            product={checkoutProduct as Product}
            initialVariantId={initialVariant?.id}>
            <CheckoutStoryItem
              item={item}
              checkoutProduct={checkoutProduct}
              onPause={onPause}
            />
          </ProductProvider>
        ) : (
          <LinkableStoryItem item={item} onPause={onPause} />
        )}
      </div>
    </section>
  );
}

function CheckoutStoryItem(
  props: Pick<ItemProps, "item" | "onPause"> & {
    checkoutProduct: ProductDataFragment;
  },
) {
  const { item, checkoutProduct, onPause } = props;

  const link = useUTMLink(item.slug);

  const { lines } = useCart();

  const { openItemModal, toggleCart } = useCartUI();

  const itemDetails = useItemDetails(item);

  const totalQuantity = lines?.reduce((acc, line) => acc + line.quantity, 0);

  return (
    <ItemContent>
      <ItemDetails
        {...itemDetails}
        clamped={true}
        price={<SelectedVariantPrice />}
        title={<ProductTitle />}
      />
      <ActionBar
        primaryActions={compact([
          {
            id: "shopify-add-to-cart",
            content: (
              <OpenAddToBagModalButton
                onClick={() => {
                  onPause();
                  openItemModal({
                    product: checkoutProduct,
                    isBuyingNow: false,
                  });
                }}
              />
            ),
          },
          totalQuantity > 0
            ? {
                id: "checkout",
                content: "Checkout",
                onAction: () => toggleCart(),
              }
            : undefined,
        ])}
        secondaryActions={
          totalQuantity > 0
            ? undefined
            : link
              ? [{ id: "learn-more", href: link, content: "Learn More" }]
              : undefined
        }
      />
    </ItemContent>
  );
}

function LinkableStoryItem(props: Pick<ItemProps, "item" | "onPause">) {
  const { item, onPause } = props;

  const link = useUTMLink(item.slug);

  const itemDetails = useItemDetails(item);

  return (
    <ItemContent>
      <ItemDetails {...itemDetails} clamped={true} />
      <ActionBar
        onAnyAction={onPause}
        primaryActions={
          link
            ? [{ id: "learn-more", href: link, content: "Learn More" }]
            : item.download_url
              ? [
                  {
                    id: "download",
                    href: item.download_url,
                    content: "Download",
                  },
                ]
              : undefined
        }
      />
    </ItemContent>
  );
}
